import { useMemo, useState } from "react";
import {
  ArrowDownLeft,
  ArrowRight,
  ArrowUpRight,
  CalendarDays,
  Check,
  CircleDollarSign,
  Download,
  FolderKanban,
  MoreHorizontal,
  Plus,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import {
  downloadFile,
  formatDate,
  formatMoney,
  invoiceTotal,
  toCSV,
} from "../../../core/src";
import type { PageProps } from "../types";
import {
  Avatar,
  Badge,
  Button,
  IconButton,
  Metric,
  Panel,
  Progress,
  EmptyState,
} from "../components/ui";
import { RevenueChart, RingChart } from "../components/charts";
import { OrbitalArtwork } from "../components/OrbitalArtwork";

export default function Dashboard({ state, navigate, notify }: PageProps) {
  const [period, setPeriod] = useState("month");
  const currency = state.settings.currency;
  const now = new Date();
  const start =
    period === "month"
      ? new Date(now.getFullYear(), now.getMonth(), 1)
      : period === "quarter"
        ? new Date(now.getFullYear(), now.getMonth() - 2, 1)
        : new Date(0);
  const orders = state.orders.filter(
    (o) => o.status !== "cancelled" && new Date(`${o.date}T00:00:00`) >= start,
  );
  const revenue = orders.reduce((sum, o) => sum + o.total, 0);
  const activeProjects = state.projects.filter((p) => p.status === "active");
  const openDeals = state.deals.filter(
    (d) => d.stage !== "lost" && d.stage !== "won",
  );
  const wonDeals = state.deals.filter((d) => d.stage === "won");
  const completion = state.tasks.length
    ? Math.round(
        (state.tasks.filter((t) => t.status === "done").length /
          state.tasks.length) *
          100,
      )
    : 0;
  const chart = useMemo(() => {
    const months = Array.from(
      { length: 6 },
      (_, i) => new Date(now.getFullYear(), now.getMonth() - 5 + i, 1),
    );
    return {
      labels: months.map((d) => d.toLocaleDateString("en", { month: "short" })),
      values: months.map((d) =>
        state.orders
          .filter(
            (o) =>
              o.status !== "cancelled" &&
              new Date(`${o.date}T00:00:00`).getMonth() === d.getMonth() &&
              new Date(`${o.date}T00:00:00`).getFullYear() === d.getFullYear(),
          )
          .reduce((sum, o) => sum + o.total, 0),
      ),
    };
  }, [state.orders, now.getFullYear(), now.getMonth()]);
  const latestOrders = [...state.orders]
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 5);
  const events = [...state.events]
    .filter(
      (e) =>
        new Date(`${e.date}T${e.time}`).getTime() + e.duration * 60_000 >=
        now.getTime(),
    )
    .sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`))
    .slice(0, 3);
  const exportReport = () => {
    downloadFile(
      "elyxir-overview.csv",
      toCSV(
        ["Metric", "Value"],
        [
          ["Revenue", revenue],
          ["Orders", orders.length],
          ["Active projects", activeProjects.length],
          ["Contacts", state.contacts.length],
          ["Task completion", `${completion}%`],
        ],
      ),
      "text/csv",
    );
    notify("Your overview report is ready.");
  };
  return (
    <div className="page-stack dashboard-page">
      <header className="dashboard-heading">
        <div className="dashboard-hero-copy">
          <p className="eyebrow">
            <span className="live-dot" /> YOUR WORKSPACE. IN FOCUS.
          </p>
          <h1>
            Everything in sync.
            <br />
            <span>Nothing in your way.</span>
          </h1>
          <p>
            Your people, projects, and possibilities. One extraordinary
            workspace.
          </p>
          <Button variant="primary" onClick={() => navigate("projects")}>
            <Plus size={17} /> Create a project <ArrowUpRight size={16} />
          </Button>
        </div>
        <div className="dashboard-hero-visual">
          <OrbitalArtwork />
          <div className="hero-readout">
            <span>
              <strong>{String(activeProjects.length).padStart(2, "0")}</strong>{" "}
              Active projects
            </span>
            <i />
            <span>
              <strong>{completion}%</strong> Tasks complete
            </span>
          </div>
        </div>
      </header>
      <div className="section-heading">
        <div className="section-title">
          <h2>Overview</h2>
          <span className="section-pill">
            {now.toLocaleDateString("en", { month: "long", year: "numeric" })}
          </span>
        </div>
        <div className="flex gap-2">
          <label className="period-select">
            <CalendarDays size={15} />
            <select
              aria-label="Overview reporting period"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              <option value="month">This month</option>
              <option value="quarter">Last 3 months</option>
              <option value="all">All time</option>
            </select>
          </label>
          <Button onClick={exportReport}>
            <Download size={15} /> <span className="hide-small">Export</span>
          </Button>
        </div>
      </div>
      <div className="stat-grid">
        <Metric
          featured
          label="Total revenue"
          value={formatMoney(revenue, currency)}
          note={`${orders.length} orders in this period`}
          icon={<CircleDollarSign size={18} />}
        />
        <Metric
          label="Active projects"
          value={String(activeProjects.length).padStart(2, "0")}
          change={`${state.projects.filter((p) => p.status === "completed").length} completed`}
          note="in your workspace"
          icon={<FolderKanban size={18} />}
        />
        <Metric
          label="Total customers"
          value={state.contacts.length.toLocaleString()}
          change={`${state.contacts.filter((c) => c.status === "active").length} active`}
          note="relationships growing"
          icon={<Users size={18} />}
        />
        <Metric
          label="Open pipeline"
          value={formatMoney(
            openDeals.reduce((s, d) => s + d.value, 0),
            currency,
          )}
          change={`${openDeals.length} opportunities`}
          note="ready for your next move"
          icon={<TrendingUp size={18} />}
        />
      </div>
      <div className="dashboard-main-grid">
        <Panel
          title="Revenue overview"
          description="A wider view of your business performance"
          action={<span className="subtle-label">Last 6 months</span>}
        >
          <div className="chart-summary">
            <strong>
              {formatMoney(
                chart.values.reduce((a, b) => a + b, 0),
                currency,
              )}
            </strong>
            <span className="chart-legend">
              <i /> Order revenue
            </span>
            <span className="chart-period">
              {chart.labels[0]} — {chart.labels.at(-1)}
            </span>
          </div>
          <RevenueChart {...chart} currency={currency} />
        </Panel>
        <Panel
          title="Project momentum"
          description="Small steps. Meaningful progress."
          className="momentum-panel"
          action={
            <IconButton
              label="View all tasks"
              onClick={() => navigate("tasks")}
            >
              <ArrowUpRight size={18} />
            </IconButton>
          }
        >
          <RingChart percent={completion} label="tasks complete" />
          <div className="momentum-stats">
            <div>
              <i className="dot-green" />
              <span>Completed</span>
              <strong>
                {state.tasks.filter((t) => t.status === "done").length}
              </strong>
            </div>
            <div>
              <i className="dot-amber" />
              <span>In progress</span>
              <strong>
                {
                  state.tasks.filter(
                    (t) => t.status === "progress" || t.status === "review",
                  ).length
                }
              </strong>
            </div>
            <div>
              <i className="dot-neutral" />
              <span>To do</span>
              <strong>
                {state.tasks.filter((t) => t.status === "todo").length}
              </strong>
            </div>
          </div>
        </Panel>
      </div>
      <div className="dashboard-secondary-grid">
        <Panel
          title="Active projects"
          description="Great things, in the making"
          action={
            <Button variant="ghost" onClick={() => navigate("projects")}>
              All projects <ArrowRight size={15} />
            </Button>
          }
        >
          <div className="project-overview-list">
            {activeProjects.slice(0, 3).map((project, i) => (
              <button
                className="project-overview"
                key={project.id}
                onClick={() => navigate("projects")}
              >
                <span className={`project-emblem emblem-${i % 3}`}>
                  <FolderKanban size={19} />
                </span>
                <span className="project-overview-info">
                  <strong>{project.name}</strong>
                  <span>
                    {project.client} <b>·</b> Due {formatDate(project.dueDate)}
                  </span>
                </span>
                <span className="project-overview-progress">
                  <span>{project.progress}%</span>
                  <Progress value={project.progress} label={project.name} />
                </span>
                <ArrowUpRight size={16} />
              </button>
            ))}
            {!activeProjects.length && (
              <EmptyState
                title="Room for your next idea"
                description="Create a project to start tracking progress."
                action={
                  <Button onClick={() => navigate("projects")}>
                    Create project
                  </Button>
                }
              />
            )}
          </div>
        </Panel>
        <section className="focus-card">
          <div className="focus-orbit" aria-hidden="true">
            <span />
            <span />
            <Zap size={25} />
          </div>
          <span className="eyebrow">LESS NOISE. MORE FOCUS.</span>
          <h2>
            Your next big thing
            <br />
            starts with a small step.
          </h2>
          <p>
            {state.tasks.filter((t) => t.status !== "done").length} tasks are
            ready for your attention.
            <br />
            Let’s make today count.
          </p>
          <Button onClick={() => navigate("tasks")}>
            Open your task board <ArrowUpRight size={16} />
          </Button>
        </section>
      </div>
      <div className="dashboard-bottom-grid">
        <Panel
          title="Recent orders"
          description="The latest activity across your business"
          action={
            <Button variant="ghost" onClick={() => navigate("orders")}>
              View all <ArrowRight size={15} />
            </Button>
          }
        >
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Order</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {latestOrders.map((order) => (
                  <tr key={order.id}>
                    <td>
                      <div className="person-cell">
                        <Avatar name={order.customer} size="sm" />
                        <div>
                          <span className="record-title">{order.customer}</span>
                          <span className="record-subtitle">{order.email}</span>
                        </div>
                      </div>
                    </td>
                    <td className="mono">{order.id.toUpperCase()}</td>
                    <td className="amount">
                      {formatMoney(order.total, currency)}
                    </td>
                    <td>
                      <Badge
                        tone={
                          order.status === "delivered"
                            ? "green"
                            : order.status === "cancelled"
                              ? "red"
                              : order.status === "shipped"
                                ? "blue"
                                : "amber"
                        }
                      >
                        {order.status}
                      </Badge>
                    </td>
                    <td>
                      <IconButton
                        label={`View order for ${order.customer}`}
                        onClick={() => navigate("orders")}
                      >
                        <ArrowUpRight size={16} />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!latestOrders.length && (
              <p className="empty-inline">
                Orders will appear here when added.
              </p>
            )}
          </div>
        </Panel>
        <Panel
          title="Coming up"
          description="A little space for what matters"
          action={
            <IconButton
              label="Open calendar"
              onClick={() => navigate("calendar")}
            >
              <CalendarDays size={18} />
            </IconButton>
          }
        >
          <div className="upcoming-list">
            {events.map((event) => (
              <button
                key={event.id}
                className="upcoming-item"
                onClick={() => navigate("calendar")}
              >
                <div className="date-tile">
                  <span>
                    {new Date(`${event.date}T12:00`).toLocaleDateString("en", {
                      month: "short",
                    })}
                  </span>
                  <strong>{new Date(`${event.date}T12:00`).getDate()}</strong>
                </div>
                <div>
                  <strong>{event.title}</strong>
                  <p>
                    {event.time} · {event.duration} min
                  </p>
                  <Badge tone={event.type === "meeting" ? "blue" : "amber"}>
                    {event.type}
                  </Badge>
                </div>
              </button>
            ))}
            {!events.length && (
              <EmptyState
                title="Your calendar is clear"
                description="Add a meeting or deadline to plan your week."
              />
            )}
          </div>
          <button
            className="text-link full-width"
            onClick={() => navigate("calendar")}
          >
            Open calendar <ArrowRight size={15} />
          </button>
        </Panel>
      </div>
      <div className="dashboard-footnote">
        <span>
          <span className="live-dot" />
          All your essentials, beautifully connected.
        </span>
        <span>Fictional demo data · Saved to your workspace</span>
      </div>
    </div>
  );
}

export function AnalyticsPage({ state, navigate }: PageProps) {
  const currency = state.settings.currency;
  const revenue = state.orders
    .filter((o) => o.status !== "cancelled")
    .reduce((s, o) => s + o.total, 0);
  const paid = state.invoices
    .filter((i) => i.status === "paid")
    .reduce((s, i) => s + invoiceTotal(i), 0);
  const won = state.deals.filter((d) => d.stage === "won").length;
  const rate = state.deals.length
    ? Math.round((won / state.deals.length) * 100)
    : 0;
  const months = Array.from(
    { length: 12 },
    (_, i) => new Date(new Date().getFullYear(), i, 1),
  );
  const values = months.map((d) =>
    state.orders
      .filter(
        (o) =>
          o.status !== "cancelled" &&
          new Date(`${o.date}T00:00:00`).getFullYear() === d.getFullYear() &&
          new Date(`${o.date}T00:00:00`).getMonth() === d.getMonth(),
      )
      .reduce((s, o) => s + o.total, 0),
  );
  const categories = [...new Set(state.products.map((p) => p.category))];
  return (
    <div className="page-stack">
      <div className="page-header">
        <div>
          <p className="eyebrow">UNDERSTAND THE BIGGER PICTURE</p>
          <h1>Business analytics</h1>
          <p className="page-description">
            A clear perspective on the numbers behind your workspace.
          </p>
        </div>
        <Button onClick={() => navigate("overview")}>
          Back to overview <ArrowUpRight size={16} />
        </Button>
      </div>
      <div className="stat-grid">
        <Metric
          featured
          label="Order revenue"
          value={formatMoney(revenue, currency)}
          note="All non-cancelled orders"
        />
        <Metric
          label="Collected invoices"
          value={formatMoney(paid, currency)}
          note="Invoices marked as paid"
        />
        <Metric
          label="Deal win rate"
          value={`${rate}%`}
          note={`${won} won of ${state.deals.length} opportunities`}
        />
        <Metric
          label="Average order"
          value={formatMoney(
            state.orders.filter((o) => o.status !== "cancelled").length
              ? revenue /
                  state.orders.filter((o) => o.status !== "cancelled").length
              : 0,
            currency,
          )}
          note="Across all valid orders"
        />
      </div>
      <Panel
        title="Annual revenue"
        description={`Order totals by month · ${new Date().getFullYear()}`}
      >
        <RevenueChart
          values={values}
          labels={months.map((d) =>
            d.toLocaleDateString("en", { month: "short" }),
          )}
          currency={currency}
        />
      </Panel>
      <div className="two-column">
        <Panel
          title="Pipeline distribution"
          description="Where your opportunities stand"
        >
          <div className="horizontal-bars">
            {(["lead", "qualified", "proposal", "won", "lost"] as const).map(
              (stage) => {
                const deals = state.deals.filter((d) => d.stage === stage);
                return (
                  <div key={stage}>
                    <div>
                      <span className="capitalize">{stage}</span>
                      <strong>
                        {formatMoney(
                          deals.reduce((s, d) => s + d.value, 0),
                          currency,
                        )}
                      </strong>
                    </div>
                    <Progress
                      label={`${stage} share`}
                      value={
                        state.deals.length
                          ? (deals.length / state.deals.length) * 100
                          : 0
                      }
                    />
                    <small>{deals.length} opportunities</small>
                  </div>
                );
              },
            )}
          </div>
        </Panel>
        <Panel
          title="Inventory value"
          description="Available stock at current list prices"
        >
          <div className="horizontal-bars">
            {categories.map((category) => {
              const value = state.products
                .filter((p) => p.category === category)
                .reduce((s, p) => s + p.stock * p.price, 0);
              const total = state.products.reduce(
                (s, p) => s + p.stock * p.price,
                0,
              );
              return (
                <div key={category}>
                  <div>
                    <span>{category}</span>
                    <strong>{formatMoney(value, currency)}</strong>
                  </div>
                  <Progress
                    label={`${category} stock share`}
                    value={total ? (value / total) * 100 : 0}
                  />
                </div>
              );
            })}
          </div>
        </Panel>
      </div>
    </div>
  );
}
